/**
 * Hook to manage file upload state and operations
 */

import { useCallback, useRef, useState } from 'react'
import { MAX_FILE_SIZE } from '../constants'
import { createImagePreview } from '../utils/fileUtils'

export interface UseFileUploadReturn {
    selectedFile: File | null
    filePreview: string | null
    fileError: string | null
    fileInputRef: React.RefObject<HTMLInputElement | null>
    handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleRemoveFile: () => void
    resetFileState: () => void
    clearFileError: () => void
    setFileError: (error: string | null) => void
}

export function useFileUpload(): UseFileUploadReturn {
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [filePreview, setFilePreview] = useState<string | null>(null)
    const [fileError, setFileError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            setFileError(null)

            // Check file size (now supporting up to 7MB via Backend Proxy)
            if (file.size > MAX_FILE_SIZE) {
                const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0)
                setFileError(`File is too large. Maximum allowed size is ${maxSizeMB}MB.`)
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                }
                return
            }

            setSelectedFile(file)

            // Create preview only for images
            if (file.type.startsWith('image/')) {
                createImagePreview(file)
                    .then(setFilePreview)
                    .catch((error) => {
                        console.error('[Chat Widget] Failed to create image preview:', error)
                        setFilePreview(null)
                    })
            } else {
                setFilePreview(null)
            }
        }
    }, [])

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null)
        setFilePreview(null)
        setFileError(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    const resetFileState = useCallback(() => {
        setSelectedFile(null)
        setFilePreview(null)
        setFileError(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    return {
        selectedFile,
        filePreview,
        fileError,
        fileInputRef,
        handleFileSelect,
        handleRemoveFile,
        resetFileState,
        clearFileError: () => setFileError(null),
        setFileError,
    }
}
